import { IBonusPagesConfig } from "@finbackoffice/fe-core";
import { useBonusPagesConfig } from "@finbackoffice/site-core";
import { IModalForwardRefPropsV2 } from "contexts";
import classNames from "classnames";
import {
    Dispatch,
    FC,
    Fragment,
    ReactNode,
    RefObject,
    SetStateAction,
    SyntheticEvent,
    useCallback,
    useMemo,
} from "react";
import { SiteContentScopes } from "@finbackoffice/enums";
import { IBannerResponse } from "@finbackoffice/clientbff-client";
import Img from "components/base/img/Img";
import Translate from "components/base/translate/Translate";
import Button from "components/base/button/Button";
import ActiveLink from "components/base/active-link/ActiveLink";
import { Carousel, CarouselSlide } from "components/base/carousel/Carousel";
import { PROMO_REGISTRY_BY_PAGE } from "utils/constants/promoRegistry";
import styles from "./promotions-list.module.sass";

type PromotionLinkProps = {
    href?: string;
    onClick: (e: SyntheticEvent<Element>) => void;
    label: ReactNode;
    text: string;
    testId: string;
    src: string;
    showText: boolean;
};

const PromotionLink: FC<PromotionLinkProps> = ({
    href,
    onClick,
    label,
    text,
    testId,
    src,
    showText,
}) => {
    const linkContent = useMemo(
        () => (
            <>
                <span>
                    {src && (
                        <Img
                            source={src}
                            width={0}
                            height={0}
                            style={{ width: "auto", height: "100%" }}
                            alt=""
                            prefixAssetsUrl={false}
                        />
                    )}
                    <span className={styles.promotionBannerLabel}>{label}</span>
                </span>
                {showText && (
                    <>
                        <span className={styles.promotionBannerTxt}>{text}</span>
                        <i className={styles.promotionBannerIcon} />
                    </>
                )}
            </>
        ),
        [label, showText, src, text],
    );
    if (href) {
        return (
            <ActiveLink
                href={href}
                data-testid={testId}
                activeClassName={styles.selected}
                className={classNames(styles.promotionBanner, "promotion-item")}>
                {linkContent}
            </ActiveLink>
        );
    }
    return (
        <Button
            type="button"
            variant="custom"
            onClick={onClick}
            data-testid={testId}
            className={classNames(styles.promotionBanner, "promotion-item")}>
            {linkContent}
        </Button>
    );
};

type IProps = {
    modalRef?: RefObject<IModalForwardRefPropsV2 | null>;
    allBanners: IBannerResponse[];
    setScope?: Dispatch<SetStateAction<SiteContentScopes>>;
    showText?: boolean;
    enableCarousel?: boolean;
};

const PromotionsList: FC<IProps> = ({
    modalRef,
    allBanners,
    setScope,
    enableCarousel = false,
    showText = true,
}) => {
    const enabledBonusesConfig = useBonusPagesConfig();

    const getBannerByScope = useCallback(
        (scope: SiteContentScopes) => {
            return allBanners.find((banner) => banner.tags.includes(scope));
        },
        [allBanners],
    );

    const getBannerData = useCallback(
        (scope: SiteContentScopes) => {
            const banner = getBannerByScope(scope);
            const bannerMedia = banner?.translations[0];
            const bannerSrc = bannerMedia?.bg_media?.url ?? "";
            const text = bannerMedia?.text || "";
            const label = bannerMedia?.title || "";

            return { bannerSrc, text, label };
        },
        [getBannerByScope],
    );

    const handleLinkClick = useCallback(
        (val: SiteContentScopes) => {
            if (modalRef) {
                setScope?.(val);
                modalRef.current?.open();
            }
        },
        [modalRef, setScope],
    );

    const renderLink = useCallback(
        ({
            href,
            bannerScope,
            testId,
            bannerSrc,
            label,
            text,
        }: {
            bannerScope: SiteContentScopes;
            testId: string;
            bannerSrc: string;
            label: ReactNode;
            text: string;
            href?: string;
        }) => (
            <PromotionLink
                href={!modalRef ? href : undefined}
                onClick={() => handleLinkClick(bannerScope)}
                testId={testId}
                src={bannerSrc}
                label={label}
                text={text}
                showText={showText}
            />
        ),
        [handleLinkClick, modalRef, showText],
    );

    const renderPromotions = useCallback(
        (item: IBonusPagesConfig) => {
            const promo = PROMO_REGISTRY_BY_PAGE[item.name];

            if (!promo?.bannerScope || !promo.pageNameTid) {
                return null;
            }

            const { bannerSrc, text, label } = getBannerData(promo.bannerScope);
            const content = renderLink({
                href: promo.href,
                bannerScope: promo.bannerScope,
                bannerSrc,
                label: label || <Translate tid={promo.pageNameTid} />,
                text,
                testId: promo.listTestId ?? "",
            });

            return item.enable ? <Fragment key={item.name}>{content}</Fragment> : null;
        },
        [getBannerData, renderLink],
    );

    if (enableCarousel) {
        return (
            <Carousel options={{ dragFree: true }}>
                {enabledBonusesConfig.map((config: IBonusPagesConfig) => (
                    <CarouselSlide key={config.name}>{renderPromotions(config)}</CarouselSlide>
                ))}
            </Carousel>
        );
    }

    return <>{enabledBonusesConfig.map(renderPromotions)}</>;
};

export default PromotionsList;
